/**
 * PDF exporter (portal side).
 *
 * Consolidated onto @dockpro/core/exporters' `buildDockPDF` — the tested,
 * canonical 2-page plan + elevation sheet (with builder branding). The portal's
 * only job is the lead-specific glue: hydrate the stored (serialized)
 * designState and pass the builder name through as branding, then delegate.
 *
 * Returns null for empty/invalid designs (no geometry) — the UI surfaces that
 * as an inline message rather than downloading an empty file.
 */
import { buildDockPDF } from "@dockpro/core/exporters";
import type { TenantConfig } from "../api";
import { hydrateDesign } from "./helpers";

interface PdfInput {
  designState: unknown;
  tenant: TenantConfig;
}

export function generatePdf({ designState, tenant }: PdfInput): Blob | null {
  // round9/embed-config — hydrate WITH the tenant's custom slip sizes.
  const state = hydrateDesign(designState, { customBoatBays: tenant.customBoatBays });
  if (!state) return null;
  // Round 23.3: pass the builder's pricing so the estimate uses real rates, and
  // the estimate-visibility flag so a builder-exported PDF (which IS sent to the
  // homeowner) honors showToCustomer just like the embed's own export.
  const doc = buildDockPDF(state, {
    productName: tenant.branding.builderName,
    pricing: tenant.pricing,
    estimate: tenant.estimate,
  });
  return doc.output("blob");
}
